import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Calendar, BookOpen, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const features = [
  {
    number: '01',
    icon: FileText,
    title: 'Project Management',
    description: 'Organize your EE, IAs, and TOK work in a single, structured workspace.'
  },
  {
    number: '02',
    icon: Calendar,
    title: 'Deadline Tracking',
    description: 'Never miss a mock submission. Export directly to Google or Apple Calendar.'
  },
  {
    number: '03',
    icon: BookOpen,
    title: 'Research Library',
    description: 'Save sources and auto-generate citations in MLA, APA, or Chicago.'
  },
  {
    number: '04',
    icon: CheckCircle,
    title: 'Progress Checklists',
    description: 'Track reflections, data collection, and every milestone with precision.'
  },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-5 h-5 bg-primary" />
            <span className="font-bold text-base tracking-tight uppercase">Coursework</span>
          </Link>
          <Button
            onClick={() => navigate('/auth')}
            size="sm"
            data-testid="header-get-started-btn"
          >
            Get Started
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-24 px-6 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto">
          <p className="label-swiss mb-6 animate-fade-in">Built for IB students</p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-foreground leading-[0.95] tracking-tight mb-8 animate-slide-up">
            Focus on<br />
            your <span className="text-primary">ideas.</span><br />
            Not the chaos.
          </h1>
          <div className="flex flex-col sm:flex-row items-start gap-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="max-w-md">
              <p className="text-base text-muted-foreground leading-relaxed">
                The systematic productivity tool for managing your Extended Essay, IAs, and TOK.
                Plan deadlines, organize research, generate citations.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:ml-auto sm:text-right">
              <Button
                size="lg"
                onClick={() => navigate('/auth')}
                className="text-base px-8"
                data-testid="hero-get-started-btn"
              >
                Start Planning Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-muted-foreground">No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline justify-between border-b border-border pb-4 mb-0">
            <p className="label-swiss">Features</p>
            <p className="text-sm text-muted-foreground">Designed for IB Diploma</p>
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border border-b border-border">
            {features.slice(0, 2).map((f) => (
              <FeatureCell key={f.number} feature={f} />
            ))}
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border border-b border-border">
            {features.slice(2, 4).map((f) => (
              <FeatureCell key={f.number} feature={f} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-foreground">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-white/50 mb-3">
              Get started today
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
              Reduce IB stress.<br />
              <span className="text-primary">Systematically.</span>
            </h2>
          </div>
          <Button
            size="lg"
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary/90 text-white text-base px-10 shrink-0"
            data-testid="cta-get-started-btn"
          >
            Get Started Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-5 px-6 bg-white border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-foreground">Coursework</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Built for IB students by Study.re
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCell = ({ feature }) => (
  <div className="p-8 group">
    <div className="flex items-start gap-4">
      <span className="font-mono text-xs text-muted-foreground mt-1 shrink-0">{feature.number}</span>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <feature.icon className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground uppercase tracking-wide">
            {feature.title}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {feature.description}
        </p>
      </div>
    </div>
  </div>
);

export default LandingPage;
